import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { PageHeader, RevealPasswordModal, AdminAccountActionModal } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const isZeroDate = value =>
  !value || String(value).startsWith('0001-01-01');

const formatCurrencyEUR = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '€0.00';
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatNumber = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 10,
  }).format(number);
};

const MarketerDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const marketer = location.state?.marketer;
  const backToUpline = location.state?.backToUpline || null;
  const breadcrumbsOverride = location.state?.breadcrumbsOverride || null;
  const [detailMarketer, setDetailMarketer] = useState(marketer || null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  // Tipe akun yang sedang di-view: 'M' (Creator/Marketer), 'S' (Shopper), 'P' (Merchant/Partner).
  // Dipakai sebagai parameter `from` saat fetch downline shopper/partner sesuai kontrak BE.
  const accountTypeFromState = location.state?.accountType;
  const accountType =
    accountTypeFromState === 'S' ||
    accountTypeFromState === 'P' ||
    accountTypeFromState === 'M'
      ? accountTypeFromState
      : 'M';

  // State for downline data
  const [downline, setDownline] = useState([]);
  const [downlineLoading, setDownlineLoading] = useState(false);
  const [downlineError, setDownlineError] = useState(null);
  const [downlineActiveTab, setDownlineActiveTab] = useState('marketer');
  const [downlinePagination, setDownlinePagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });
  const lastFetchedIdRef = useRef(null);

  // BE masks sensitive fields by default; admin opt-in via reveal-verify
  // (POST /api/v1/account/admin/reveal-verify) elevates session for 5 min.
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [actionModal, setActionModal] = useState({ isOpen: false, type: 'delete' });
  const [actionSuccess, setActionSuccess] = useState(null);
  // `revealed` is only authoritative AFTER fresh detail fetch; list endpoint
  // doesn't include it, so gate the toggle button on detailFetched to prevent
  // a Show-button flash on refresh.
  const [detailFetched, setDetailFetched] = useState(false);
  // Local visual hide on top of BE-raw data while elevation is still active.
  // Toggling does NOT call the API; BE stays revealed until TTL ends.
  const [localHidden, setLocalHidden] = useState(false);

  const revealed = detailMarketer?.revealed === true;
  const effectivelyMasked = !revealed || localHidden;

  const maskTail = (value, keep = 4) => {
    if (!value) return '-';
    const str = String(value);
    if (str.length <= keep) return '*'.repeat(Math.max(2, str.length));
    return `**${str.slice(-keep)}`;
  };
  const maskFull = value => (value ? '******' : '-');
  // Pre-fetch we render a blank placeholder so list-state values never leak
  // raw. Using a non-breaking space keeps `|| '-'` / `|| 'N/A'` fallbacks
  // from kicking in, which would otherwise look like "no data" while loading.
  const sensitiveValue = (value, masker = maskFull) => {
    if (!detailFetched) return '​';
    return revealed && localHidden ? masker(value) : value;
  };

  const handleToggleReveal = () => {
    if (revealed) {
      setLocalHidden(prev => !prev);
    } else {
      setRevealModalOpen(true);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [id]);

  // Keep local marketer details in sync with route changes
  useEffect(() => {
    setDetailMarketer(marketer || null);
    setDetailFetched(false);
    setLocalHidden(false);
  }, [id, marketer]);

  const fetchMarketerDetails = useCallback(async () => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError(null);
    try {
      const response = await marketerService.getFormattedMarketerDetails(id);
      if (response && response.data) {
        setDetailMarketer(response.data);
        setDetailFetched(true);
      }
    } catch (err) {
      console.error('Failed to fetch marketer details:', err);
      if (err instanceof ApiError) {
        setDetailError(`Failed to load creator details: ${err.message}`);
      } else {
        setDetailError('Failed to load creator details. Please try again.');
      }
    } finally {
      setDetailLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchMarketerDetails();
  }, [fetchMarketerDetails]);

  const handleAdminActionSubmit = async ({ reason, password }) => {
    if (!detailMarketer?.id) return;
    if (actionModal.type === 'delete') {
      await marketerService.deleteMarketerAdmin(detailMarketer.id, { reason, password });
      setActionSuccess('Creator account has been immediately deleted.');
    } else {
      await marketerService.unblockMarketerAdmin(detailMarketer.id, { reason, password });
      setActionSuccess('Creator account has been unblocked. 6-month re-registration block lifted.');
    }
    await fetchMarketerDetails();
  };

  const isDeleted = detailMarketer?.accountStatusCode === 'D';
  const deletedDate = detailMarketer?.deleted ? new Date(detailMarketer.deleted) : null;
  const cooldownUntil = deletedDate
    ? new Date(new Date(deletedDate).setMonth(deletedDate.getMonth() + 6))
    : null;
  const isCooldownActive = cooldownUntil ? new Date() < cooldownUntil : false;
  const creatorFullName =
    [detailMarketer?.firstName, detailMarketer?.lastName].filter(Boolean).join(' ') ||
    detailMarketer?.fullName ||
    'Creator';

  // Fetch marketer downline
  const fetchMarketerDownline = useCallback(
    async (page = 1, tabOverride = null) => {
      if (!detailMarketer?.accountId) return;

      try {
        setDownlineLoading(true);
        setDownlineError(null);

        const activeTab = tabOverride || downlineActiveTab;
        let response;
        if (activeTab === 'shopper') {
          response = await marketerService.getFormattedShopperDownline(
            detailMarketer.accountId,
            {
              page,
              size: downlinePagination.size,
              order: 'created_at DESC',
              from: accountType,
            }
          );
        } else if (activeTab === 'partner') {
          response = await marketerService.getFormattedPartnerDownline(
            detailMarketer.accountId,
            {
              page,
              size: downlinePagination.size,
              order: 'created_at DESC',
              from: accountType,
            }
          );
        } else {
          response = await marketerService.getFormattedMarketerDownline(
            detailMarketer.accountId,
            { page, size: downlinePagination.size, order: 'created_at DESC' }
          );
        }

        if (response && response.data) {
          setDownline(response.data.data || []);
          setDownlinePagination({
            page: response.data.page || 1,
            pageCount: response.data.pageCount || 1,
            size: response.data.size || 20,
            count: response.data.count || 0,
            total: response.data.total || 0,
          });
        }
      } catch (err) {
        console.error('Failed to fetch marketer downline:', err);
        if (err instanceof ApiError) {
          setDownlineError(`Failed to load downline: ${err.message}`);
        } else {
          setDownlineError('Failed to load downline. Please try again.');
        }
        setDownline([]);
      } finally {
        setDownlineLoading(false);
      }
    },
    [
      detailMarketer?.accountId,
      downlinePagination.size,
      downlineActiveTab,
      accountType,
    ]
  );

  // Handle downline pagination
  const handleDownlinePageChange = useCallback(
    newPage => {
      fetchMarketerDownline(newPage);
    },
    [fetchMarketerDownline]
  );

  // Fetch downline when marketer ID changes (once per marketer)
  useEffect(() => {
    if (!detailMarketer?.accountId) return;
    if (lastFetchedIdRef.current === detailMarketer.accountId) return;
    lastFetchedIdRef.current = detailMarketer.accountId;

    // Reset state for the new marketer's downline
    setDownline([]);
    setDownlinePagination(prev => ({
      ...prev,
      page: 1,
      pageCount: 1,
      count: 0,
      total: 0,
    }));
    fetchMarketerDownline(1); // Fetch the first page of the new downline
  }, [id, detailMarketer?.accountId]);

  // Handle downline tab change
  const handleDownlineTabChange = useCallback(
    newTab => {
      if (newTab === downlineActiveTab) return;
      setDownlineActiveTab(newTab);
      setDownline([]);
      setDownlinePagination(prev => ({
        ...prev,
        page: 1,
        pageCount: 1,
        count: 0,
        total: 0,
      }));
      // Fetch immediately with selected tab
      fetchMarketerDownline(1, newTab);
    },
    [downlineActiveTab, fetchMarketerDownline]
  );

  // Removed the effect that fetched on active tab change to avoid duplicate initial calls

  const detailMarketerLabel =
    detailMarketer?.fullName ||
    [detailMarketer?.firstName, detailMarketer?.lastName]
      .filter(Boolean)
      .join(' ') ||
    detailMarketer?.firstName ||
    'Detail';

  // Add breadcrumbs for navigation context
  const breadcrumbs =
    breadcrumbsOverride ||
    [
      { name: 'Home', href: ROUTES.DASHBOARD },
      { name: 'Creators', href: ROUTES.MARKETERS },
      { name: detailMarketerLabel },
    ];

  const handleOpenEarningsBonuses = useCallback(() => {
    if (!detailMarketer) return;
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_EARNINGS_BONUSES, {
        id: detailMarketer.id || id || detailMarketer.accountId,
      }),
      { state: { marketer: detailMarketer, accountType } }
    );
  }, [accountType, detailMarketer, id, navigate]);

  const handleEditBank = useCallback(() => {
    if (!detailMarketer) return;
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_EDIT_BANK, {
        id: detailMarketer.id || id || detailMarketer.accountId,
      }),
      { state: { marketer: detailMarketer } }
    );
  }, [detailMarketer, id, navigate]);

  const handleEditPersonal = useCallback(() => {
    if (!detailMarketer) return;
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_EDIT_PERSONAL, {
        id: detailMarketer.id || id || detailMarketer.accountId,
      }),
      { state: { marketer: detailMarketer } }
    );
  }, [detailMarketer, id, navigate]);

  const handleEditPartitaIva = useCallback(() => {
    if (!detailMarketer) return;
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_EDIT_PARTITA_IVA, {
        id: detailMarketer.id || id || detailMarketer.accountId,
      }),
      { state: { marketer: detailMarketer } }
    );
  }, [detailMarketer, id, navigate]);

  const handleEditCodiceFiscale = useCallback(() => {
    if (!detailMarketer) return;
    navigate(
      ROUTES.generatePath(ROUTES.MARKETERS_EDIT_CODICE_FISCALE, {
        id: detailMarketer.id || id || detailMarketer.accountId,
      }),
      { state: { marketer: detailMarketer } }
    );
  }, [detailMarketer, id, navigate]);

  const handleDownlineDetail = useCallback(
    member => {
      if (!member?.id) return;

      const currentRoute = ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, {
        id,
      });
      const currentBreadcrumbs =
        breadcrumbsOverride ||
        [
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Creators', href: ROUTES.MARKETERS },
          { name: detailMarketerLabel, href: currentRoute },
        ];

      // Build "Back to Upline" as a stack: each level points back to the
      // immediate previous page, not the topmost upline.
      const nextBackToUpline = {
        label: 'Back to Upline',
        to: currentRoute,
        state: {
          marketer: detailMarketer,
          accountType,
          breadcrumbsOverride: currentBreadcrumbs,
          backToUpline: backToUpline || null,
        },
      };

      if (downlineActiveTab === 'partner') {
        const merchantCompany =
          member.companyName ||
          member.fullName ||
          [member.firstName, member.lastName].filter(Boolean).join(' ');

        const targetCrumbs = [
          ...currentBreadcrumbs,
          {
            name: merchantCompany || member.id,
            href: ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id: member.id }),
          },
        ];
        navigate(
          ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id: member.id }),
          {
            state: {
              backToUpline: nextBackToUpline,
              breadcrumbsOverride: targetCrumbs,
              merchant: {
                ...member,
                companyName: merchantCompany,
                isActive:
                  typeof member.isActive === 'boolean'
                    ? member.isActive
                    : member.accountStatusLabel === 'Active' ||
                      member.status === 'Active',
              },
            },
          }
        );
        return;
      }

      if (downlineActiveTab === 'shopper') {
        const shopperLabel =
          member.fullName ||
          [member.firstName, member.lastName].filter(Boolean).join(' ') ||
          member.id;
        const targetCrumbs = [
          ...currentBreadcrumbs,
          {
            name: shopperLabel,
            href: ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }),
          },
        ];
        navigate(
          ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: member.id }),
          {
            state: {
              shopper: member,
              backToUpline: nextBackToUpline,
              breadcrumbsOverride: targetCrumbs,
            },
          }
        );
        return;
      }

      const creatorLabel =
        member.fullName ||
        [member.firstName, member.lastName].filter(Boolean).join(' ') ||
        member.id;
      const targetCrumbs = [
        ...currentBreadcrumbs,
        {
          name: creatorLabel,
          href: ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id: member.id }),
        },
      ];
      navigate(
        ROUTES.generatePath(ROUTES.MARKETERS_DETAIL_LIST, { id: member.id }),
        {
          state: {
            marketer: member,
            accountType: 'M',
            backToUpline: nextBackToUpline,
            breadcrumbsOverride: targetCrumbs,
          },
        }
      );
    },
    [
      accountType,
      backToUpline,
      breadcrumbsOverride,
      detailMarketer,
      detailMarketerLabel,
      downlineActiveTab,
      id,
      navigate,
    ]
  );

  if (detailLoading && !detailMarketer) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='flex items-center space-x-2 text-center'>
          <div>
            <svg
              className='animate-spin h-8 w-8 text-blue-600 mx-auto mb-2'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              ></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              ></path>
            </svg>
            <p className='text-gray-600'>Loading creator details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!detailMarketer) {
    return (
      <div className='flex items-center justify-center min-h-64'>
        <div className='text-center'>
          <p className='text-sm text-red-600 mb-4'>
            {detailError || 'Creator data not found.'}
          </p>
          <button
            type='button'
            onClick={() => navigate(ROUTES.MARKETERS)}
            className='px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700'
          >
            Back to Creators
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <PageHeader
        title='Creator Details'
        subtitle='View detailed information about this creator and their downline'
        breadcrumbs={breadcrumbs}
      />

      {detailError && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
          <p className='text-sm text-red-700'>{detailError}</p>
        </div>
      )}

      {actionSuccess && (
        <div className='bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between'>
          <p className='text-sm text-green-800 font-medium'>{actionSuccess}</p>
          <button
            type='button'
            onClick={() => setActionSuccess(null)}
            className='text-green-600 hover:text-green-800 text-sm font-medium'
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Creator Details Section  */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <div className='flex items-start justify-between gap-4'>
            <div className='min-w-0'>
              <h3 className='text-lg font-semibold text-gray-900 truncate'>
                {[detailMarketer?.firstName, detailMarketer?.lastName]
                  .filter(Boolean)
                  .join(' ') || 'Creator'}
              </h3>
              <p className='text-xs text-gray-500 mt-0.5 break-all'>
                Creator ID: {detailMarketer?.id || '-'}
              </p>
            </div>
            <div className='flex items-center gap-2 flex-shrink-0'>
              {isDeleted ? (
                <>
                  <span className='inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700'>
                    Deleted
                  </span>
                  {detailMarketer?.unblockedAt && (
                    <span className='inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700'>
                      Unblocked
                    </span>
                  )}
                </>
              ) : (
                <>
                  {detailMarketer?.accountStatusLabel && (
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                        detailMarketer.accountStatusLabel === 'Active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {detailMarketer.accountStatusLabel}
                    </span>
                  )}
                  {typeof detailMarketer?.plus === 'boolean' && (
                    <span
                      className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                        detailMarketer.plus
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {detailMarketer.plus ? 'Plus' : 'Basic'}
                    </span>
                  )}
                </>
              )}
              {detailFetched && (
                <button
                  type='button'
                  onClick={handleToggleReveal}
                  className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md'
                >
                  {effectivelyMasked ? (
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                      />
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                      />
                    </svg>
                  ) : (
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                      />
                    </svg>
                  )}
                  {effectivelyMasked ? 'Show' : 'Hide'}
                </button>
              )}
              {isDeleted && !detailMarketer?.unblockedAt && isCooldownActive && (
                <button
                  type='button'
                  onClick={() => setActionModal({ isOpen: true, type: 'unblock' })}
                  className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z' />
                  </svg>
                  Unblock
                </button>
              )}
              {!isDeleted && (
                <button
                  type='button'
                  onClick={() => setActionModal({ isOpen: true, type: 'delete' })}
                  className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md shadow-sm'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                  Delete
                </button>
              )}
              <button
                type='button'
                onClick={() => setIsCollapsed(prev => !prev)}
                aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                className='p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md'
              >
                <svg
                  className={`w-5 h-5 transition-transform ${
                    isCollapsed ? '' : 'rotate-180'
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M19 9l-7 7-7-7'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {!isCollapsed && (
          <div className='p-6 space-y-8'>
            {isDeleted && (
              <div className='rounded-lg border border-red-200 bg-red-50/60 p-4'>
                <div className='flex items-start gap-3'>
                  <div className='p-2 rounded-full bg-red-100 text-red-600 flex-shrink-0'>
                    <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                    </svg>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <h4 className='text-sm font-semibold text-red-900'>
                      Deleted Creator Account
                    </h4>
                    <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-xs'>
                      <div>
                        <span className='text-gray-500'>Deleted At:</span>{' '}
                        <span className='font-medium text-gray-800'>
                          {detailMarketer?.deleted ? new Date(detailMarketer.deleted).toLocaleString() : '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>Deletion Reason:</span>{' '}
                        <span className='font-medium text-gray-800'>
                          {detailMarketer?.deletionReason || '-'}
                        </span>
                      </div>
                      <div>
                        <span className='text-gray-500'>6-Month Re-reg Block:</span>{' '}
                        {detailMarketer?.unblockedAt ? (
                          <span className='font-medium text-emerald-700'>
                            LIFTED (Unblocked)
                          </span>
                        ) : isCooldownActive ? (
                          <span className='font-medium text-red-700'>
                            ACTIVE until {cooldownUntil ? cooldownUntil.toLocaleDateString() : '-'}
                          </span>
                        ) : (
                          <span className='font-medium text-gray-600'>Expired</span>
                        )}
                      </div>
                      {detailMarketer?.unblockedAt && (
                        <>
                          <div>
                            <span className='text-gray-500'>Unblocked At:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {new Date(detailMarketer.unblockedAt).toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span className='text-gray-500'>Unblocked By:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {detailMarketer.unblockedByName || detailMarketer.unblockedBy || '-'}
                            </span>
                          </div>
                          <div className='sm:col-span-2 lg:col-span-1'>
                            <span className='text-gray-500'>Unblock Reason:</span>{' '}
                            <span className='font-medium text-gray-800'>
                              {detailMarketer.unblockReason || '-'}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Personal Information */}
            <div>
              <div className='border-b pb-2 mb-4'>
                <div className='flex items-center gap-2'>
                  <h4 className='text-base font-semibold text-gray-800'>
                    Personal Information
                  </h4>
                  <button
                    type='button'
                    onClick={handleEditPersonal}
                    aria-label='Edit Personal Information'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                  </button>
                </div>
                {/* TODO(BE-wiring): replace static placeholder with audit data */}
                {/* <p className='text-xs text-gray-500 mt-1'>
                  Last Modified{' '}
                  <span className='text-gray-700 font-medium'>3/25/2026</span>
                </p> */}
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    First Name
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.firstName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Last Name
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.lastName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Phone Number
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {sensitiveValue(detailMarketer?.phone, maskTail) || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Email Address
                  </label>
                  <p className='text-sm text-gray-700 mt-1 break-all'>
                    {sensitiveValue(detailMarketer?.email) || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Birthdate
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {!detailFetched
                      ? ' '
                      : effectivelyMasked
                      ? '******'
                      : detailMarketer?.dateOfBirth &&
                        !isZeroDate(detailMarketer.dateOfBirth)
                      ? moment(detailMarketer.dateOfBirth).format(
                          'MMM, DD YYYY'
                        )
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Join Date
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.joinDate
                      ? moment(detailMarketer.joinDate).format('MMM, DD YYYY')
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Account & Subscriptions */}
            <div>
              <h4 className='text-base font-semibold text-gray-800 border-b pb-2 mb-4'>
                Account & Subscriptions
              </h4>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Account Type
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {typeof detailMarketer?.plus === 'boolean'
                      ? detailMarketer.plus
                        ? 'Plus'
                        : 'Basic'
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Subscription Date
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.paidStart &&
                    detailMarketer.paidStart !== '0001-01-01T00:00:00Z'
                      ? moment(detailMarketer.paidStart).format('MMM, DD YYYY')
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Subscription Due Date
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.paidExpired &&
                    detailMarketer.paidExpired !== '0001-01-01T00:00:00Z'
                      ? moment(detailMarketer.paidExpired).format(
                          'MMM, DD YYYY'
                        )
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Account Status
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.accountStatusLabel || '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Earnings & Bonuses */}
            <div>
              <div className='border-b pb-2 mb-4'>
                <div className='flex items-center gap-2'>
                  <h4 className='text-base font-semibold text-gray-800'>
                    Earnings & Bonuses
                  </h4>
                  {/* TODO(BE-wiring): wire to detail/external page when ready */}
                  <button
                    type='button'
                    onClick={handleOpenEarningsBonuses}
                    aria-label='Open Earnings & Bonuses'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                      />
                    </svg>
                  </button>
                </div>
                {/* TODO(BE-wiring): replace static placeholder with audit data */}
                {/* <p className='text-xs text-gray-500 mt-1'>
                  Last Modified{' '}
                  <span className='text-gray-700 font-medium'>3/25/2026</span>
                </p> */}
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Bonus Level
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.careerLevelLabel || '-'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Volume Point (VP)
                  </label>
                  <p className='text-sm text-gray-700 mt-1 font-semibold'>
                    {downlineActiveTab === 'partner' ||
                    downlineActiveTab === 'shopper'
                      ? 'N/A'
                      : formatNumber(detailMarketer?.careerPoint)}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Marketing Boost
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {formatCurrencyEUR(detailMarketer?.marketingBoost)}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Volume Bonus & Commission
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {formatCurrencyEUR(detailMarketer?.careerCommission)}
                  </p>
                </div>
              </div>
            </div>

            {/* Bank Account */}
            <div>
              <div className='border-b pb-2 mb-4'>
                <h4 className='text-base font-semibold text-gray-800'>
                  Bank Account
                </h4>
                {/* TODO(BE-wiring): replace static placeholder with audit data */}
                {/* <p className='text-xs text-gray-500 mt-1'>
                  Last Modified{' '}
                  <span className='text-gray-700 font-medium'>3/25/2026</span>
                </p> */}
              </div>
              {/* Bank (sub-section) — IBAN + Beneficiary; edit hits its own page */}
              <div>
                <div className='bg-gray-50 px-4 py-2 rounded-md mb-4 flex items-center gap-2'>
                  <h5 className='text-sm font-semibold text-gray-800'>Bank</h5>
                  <button
                    type='button'
                    onClick={handleEditBank}
                    aria-label='Edit Bank'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6'>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      IBAN
                    </label>
                    <p className='text-sm text-gray-700 mt-1 break-all'>
                      {sensitiveValue(
                        detailMarketer?.iban ||
                          detailMarketer?.bankAccountNumber,
                        maskTail
                      ) || '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Beneficiary Name
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {sensitiveValue(detailMarketer?.beneficiaryName) || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Codice Fiscale (sub-section) — always shown so admin can add
                  a new CF via edit; CF address writes to MAIN address columns
                  on marketer (per release notes), so display from those. */}
              <div className='mt-6'>
                <div className='bg-gray-50 px-4 py-2 rounded-md mb-4 flex items-center gap-2'>
                  <h5 className='text-sm font-semibold text-gray-800'>
                    Codice Fiscale
                  </h5>
                  <button
                    type='button'
                    onClick={handleEditCodiceFiscale}
                    aria-label='Edit Codice Fiscale'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Codice Fiscale
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {sensitiveValue(
                        detailMarketer?.codiceFiscale,
                        maskTail
                      ) || '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Address
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {sensitiveValue(
                        detailMarketer?.codiceFiscaleAddress ||
                          detailMarketer?.address
                      ) || '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Province
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.codiceFiscaleProvince ||
                        detailMarketer?.province ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      City
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.codiceFiscaleCity ||
                        detailMarketer?.city ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      ZIP Code
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.codiceFiscaleZipCode ||
                        detailMarketer?.zip ||
                        '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Partita Iva (sub-section) — always shown so admin can add
                  a new PIVA via edit; values fall back to billing* fields. */}
              <div className='mt-6'>
                <div className='bg-gray-50 px-4 py-2 rounded-md mb-4 flex items-center gap-2'>
                  <h5 className='text-sm font-semibold text-gray-800'>
                    Partita Iva
                  </h5>
                  <button
                    type='button'
                    onClick={handleEditPartitaIva}
                    aria-label='Edit Partita Iva'
                    className='text-blue-600 hover:text-blue-700'
                  >
                    <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                  </button>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Partita Iva
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {sensitiveValue(
                        detailMarketer?.partitaIva || detailMarketer?.vat,
                        maskTail
                      ) || '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Address
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {sensitiveValue(
                        detailMarketer?.partitaIvaAddress ||
                          detailMarketer?.billingAddress
                      ) || '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      Province
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.partitaIvaProvince ||
                        detailMarketer?.billingProvince ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      City
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.partitaIvaCity ||
                        detailMarketer?.billingCity ||
                        '-'}
                    </p>
                  </div>
                  <div>
                    <label className='text-sm font-medium text-gray-900'>
                      ZIP Code
                    </label>
                    <p className='text-sm text-gray-700 mt-1'>
                      {detailMarketer?.partitaIvaZipCode ||
                        detailMarketer?.billingZipCode ||
                        '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Network */}
            <div>
              <div className='border-b pb-2 mb-4'>
                <h4 className='text-base font-semibold text-gray-800'>
                  Network
                </h4>
                {/* TODO(BE-wiring): replace static placeholder with audit data */}
                {/* <p className='text-xs text-gray-500 mt-1'>
                  Last Modified{' '}
                  <span className='text-gray-700 font-medium'>3/25/2026</span>
                </p> */}
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6'>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Upline
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.upline || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Direct Partner
                  </label>
                  <p className='text-sm text-gray-700 mt-1'>
                    {detailMarketer?.partner || 0}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-gray-900'>
                    Total Downline
                  </label>
                  <p className='text-sm text-gray-700 mt-1 font-semibold'>
                    {detailMarketer?.downline || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Downline Section  */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
            Downline
            {downlineLoading && (
              <svg
                className='animate-spin h-4 w-4 text-blue-600'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                ></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            )}
          </h3>

          {/* Tabs. Internal keys (marketer/partner/shopper) preserved for
              fetch contracts; only display labels are renamed per design. */}
          <div className='mt-4 border-b border-gray-200'>
            <nav
              className='flex -mb-px space-x-6'
              role='tablist'
              aria-label='Downline tabs'
            >
              <button
                type='button'
                onClick={() => handleDownlineTabChange('marketer')}
                className={`${
                  downlineActiveTab === 'marketer'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Creator
              </button>
              <button
                type='button'
                onClick={() => handleDownlineTabChange('partner')}
                className={`${
                  downlineActiveTab === 'partner'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Merchant
              </button>
              <button
                type='button'
                onClick={() => handleDownlineTabChange('shopper')}
                className={`${
                  downlineActiveTab === 'shopper'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium`}
              >
                Shopper
              </button>
            </nav>
          </div>

          {downlinePagination.count > 0 && (
            <p className='text-sm text-gray-500 mt-3'>
              Showing{' '}
              {(downlinePagination.page - 1) * downlinePagination.size + 1} to{' '}
              {Math.min(
                downlinePagination.page * downlinePagination.size,
                downlinePagination.count
              )}{' '}
              of {downlinePagination.count} downline members
            </p>
          )}
        </div>

        {/* Error Display */}
        {downlineError && (
          <div className='px-6 py-4 border-b border-gray-200'>
            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
              <div className='flex items-center'>
                <svg
                  className='w-5 h-5 text-red-400 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <p className='text-sm text-red-700'>{downlineError}</p>
                <button
                  onClick={() => setDownlineError(null)}
                  className='ml-auto text-red-400 hover:text-red-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Merchant tab uses a different column layout (company name +
            balances + Active/Inactive status); Creator/Shopper share one. */}
        {(() => {
          const isMerchantTab = downlineActiveTab === 'partner';
          const colSpan = isMerchantTab ? 10 : 9;
          return (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56'>
                      ID
                    </th>
                    {isMerchantTab ? (
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                        Company Name
                      </th>
                    ) : (
                      <>
                        <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          First Name
                        </th>
                        <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Last Name
                        </th>
                      </>
                    )}
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Join Date
                    </th>
                    <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Referral Code
                    </th>
                    {isMerchantTab && (
                      <>
                        <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Marketing Balance
                        </th>
                        <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                          Marketing Bonus Balance
                        </th>
                      </>
                    )}
                    <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Direct Partner
                    </th>
                    <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Downline
                    </th>
                    <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {downline.length === 0 && !downlineLoading ? (
                    <tr>
                      <td
                        colSpan={colSpan}
                        className='px-6 py-8 text-center text-gray-500'
                      >
                        <div className='flex flex-col items-center'>
                          <svg
                            className='w-12 h-12 text-gray-300 mb-3'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                            />
                          </svg>
                          <p className='text-lg font-medium'>
                            No downline members found
                          </p>
                          <p className='text-sm'>
                            This creator doesn't have any downline members yet.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    downline.map((member, index) => {
                      // Status badge: Plus/Active = blue, Basic/Inactive = gray.
                      const statusLabel = isMerchantTab
                        ? member.accountStatusLabel || member.status || '-'
                        : member.status || '-';
                      const isHighlightStatus =
                        statusLabel === 'Plus' || statusLabel === 'Active';
                      // Merchant uses companyName; fall back to fullName if BE
                      // hasn't supplied a dedicated companyName yet.
                      const merchantCompany =
                        member.companyName ||
                        member.fullName ||
                        [member.firstName, member.lastName]
                          .filter(Boolean)
                          .join(' ') ||
                        '-';
                      return (
                        <tr key={index} className='hover:bg-gray-50 align-top'>
                          <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                            <div className='break-all whitespace-normal' title={member.id}>
                              {member.id}
                            </div>
                          </td>
                          {isMerchantTab ? (
                            <td className='px-4 py-4 align-top'>
                              <div
                                className='text-sm font-medium text-gray-900 max-w-[220px] break-words'
                                title={merchantCompany}
                              >
                                {merchantCompany}
                              </div>
                            </td>
                          ) : (
                            <>
                              <td className='px-4 py-4 align-top'>
                                <div
                                  className='text-sm font-medium text-gray-900 max-w-[220px] break-words'
                                  title={member.firstName}
                                >
                                  {member.firstName}
                                </div>
                                <div
                                  className='md:hidden text-xs text-gray-500 max-w-[220px] break-words'
                                  title={member.lastName}
                                >
                                  {member.lastName}
                                </div>
                              </td>
                              <td className='px-3 py-4 text-sm font-medium text-gray-900 align-top'>
                                <div
                                  className='max-w-[220px] break-words'
                                  title={member.lastName}
                                >
                                  {member.lastName}
                                </div>
                              </td>
                            </>
                          )}
                          <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 align-top'>
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                isHighlightStatus
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {statusLabel}
                            </span>
                          </td>
                          <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 align-top'>
                            {member.joinDate}
                          </td>
                          <td className='px-3 py-4 text-xs text-gray-900 font-mono align-top'>
                            <div
                              className='break-all whitespace-normal'
                              title={member.referralCode}
                            >
                              {member.referralCode}
                            </div>
                          </td>
                          {isMerchantTab && (
                            <>
                              <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right align-top'>
                                {formatCurrencyEUR(
                                  member.marketingBalance ??
                                    member.marketingBoost ??
                                    member.balance
                                )}
                              </td>
                              <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right align-top'>
                                {formatCurrencyEUR(member.marketingBonusBalance)}
                              </td>
                            </>
                          )}
                          <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                            {member.partner || 0}
                          </td>
                          <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold align-top'>
                            {member.downline || 0}
                          </td>
                          <td className='px-3 py-4 whitespace-nowrap text-center align-top'>
                            <button
                              onClick={() => handleDownlineDetail(member)}
                              className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                            >
                              Detail
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Downline Pagination */}
        {downlinePagination.pageCount > 1 && (
          <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
            <div className='flex-1 flex justify-between sm:hidden'>
              <button
                onClick={() =>
                  handleDownlinePageChange(downlinePagination.page - 1)
                }
                disabled={downlinePagination.page <= 1 || downlineLoading}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() =>
                  handleDownlinePageChange(downlinePagination.page + 1)
                }
                disabled={
                  downlinePagination.page >= downlinePagination.pageCount ||
                  downlineLoading
                }
                className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
            <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm text-gray-700'>
                  Page{' '}
                  <span className='font-medium'>{downlinePagination.page}</span>{' '}
                  of{' '}
                  <span className='font-medium'>
                    {downlinePagination.pageCount}
                  </span>
                </p>
              </div>
              <div>
                <nav
                  className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                  aria-label='Pagination'
                >
                  <button
                    onClick={() =>
                      handleDownlinePageChange(downlinePagination.page - 1)
                    }
                    disabled={downlinePagination.page <= 1 || downlineLoading}
                    className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Previous</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, downlinePagination.pageCount) },
                    (_, i) => {
                      let pageNum;
                      if (downlinePagination.pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (downlinePagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (
                        downlinePagination.page >=
                        downlinePagination.pageCount - 2
                      ) {
                        pageNum = downlinePagination.pageCount - 4 + i;
                      } else {
                        pageNum = downlinePagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handleDownlinePageChange(pageNum)}
                          disabled={downlineLoading}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNum === downlinePagination.page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() =>
                      handleDownlinePageChange(downlinePagination.page + 1)
                    }
                    disabled={
                      downlinePagination.page >= downlinePagination.pageCount ||
                      downlineLoading
                    }
                    className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Next</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}

        {backToUpline?.to && (
          <div className='px-6 py-4 border-t border-gray-200 flex items-center justify-end'>
            <button
              type='button'
              onClick={() =>
                navigate(backToUpline.to, { state: backToUpline.state })
              }
              className='inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              {backToUpline.label || 'Back to Upline'}
            </button>
          </div>
        )}
      </div>

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => setRevealModalOpen(false)}
        onSuccess={() => fetchMarketerDetails()}
      />

      <AdminAccountActionModal
        isOpen={actionModal.isOpen}
        onClose={() => setActionModal(prev => ({ ...prev, isOpen: false }))}
        onSubmit={handleAdminActionSubmit}
        actionType={actionModal.type}
        accountType='Creator'
        accountName={creatorFullName}
      />
    </div>
  );
};

export default MarketerDetailPage;
